import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useNavigate } from "react-router-dom";
import { Wallet, Search, ShoppingCart, TrendingUp, ArrowRight, Coins } from "lucide-react";

const steps = [
  {
    number: "01",
    title: "Get a Wallet",
    description:
      "Install Phantom or Solflare on your desktop or mobile device. Connect to FLOW by clicking the Connect button — no SOL or funds needed to get started.",
    icon: Wallet,
  },
  {
    number: "02",
    title: "Browse & Discover",
    description:
      "Explore the marketplace to find NFTs you love. Filter by trending, newest, or most liked to discover unique digital art.",
    icon: Search,
  },
  {
    number: "03",
    title: "Buy or Mint",
    description:
      "Your first NFT mint is completely free — no SOL or EMON needed! To buy other NFTs, you'll need EMON tokens. You can get EMON by swapping SOL on Raydium. Prices are shown in USD for convenience.",
    icon: ShoppingCart,
  },
  {
    number: "04",
    title: "Trade & Earn",
    description:
      "When someone buys your NFT, you receive EMON directly to your wallet. Every resale increases the price automatically via the bonding curve — the earlier you buy, the more you can earn.",
    icon: TrendingUp,
  },
];

const bondingPrices = [
  { sale: "Mint", price: "FREE", width: "15%" },
  { sale: "Sale 1", price: "$1.00", width: "22%" },
  { sale: "Sale 2", price: "$1.26", width: "35%" },
  { sale: "Sale 3", price: "$1.59", width: "45%" },
  { sale: "Sale 4", price: "$1.99", width: "55%" },
  { sale: "Sale 5", price: "$2.50", width: "67%" },
  { sale: "Sale 6", price: "$3.13", width: "82%" },
  { sale: "Sale 7", price: "$3.92", width: "100%" },
];

const faqItems = [
  {
    question: "Is it free to start?",
    answer:
      "Yes! Every wallet gets one free NFT mint — no SOL or EMON needed. When you want to buy other NFTs, you'll need to swap SOL for EMON on Raydium.",
  },
  {
    question: "What wallet do I need?",
    answer:
      "You need a Solana-compatible wallet like Phantom or Solflare. Both are available as browser extensions and mobile apps.",
  },
  {
    question: "How is the price determined?",
    answer:
      "Each NFT follows a bonding curve: after every sale, the price automatically increases by 25% + $0.01. This means early buyers get the best prices.",
  },
  {
    question: "Can I sell my NFT?",
    answer:
      "Absolutely! You can list any NFT you own on the marketplace. When someone buys it, you receive the sale amount directly to your wallet.",
  },
  {
    question: "What blockchain does FLOW use?",
    answer:
      "FLOW NFT runs on Solana — one of the fastest and cheapest blockchains. Transactions settle in seconds with minimal fees.",
  },
  {
    question: "What is EMON?",
    answer:
      "EMON is the native token of the FLOW ecosystem. All NFT prices are denominated in EMON, though displayed in USD for convenience. You can swap SOL for EMON on Raydium.",
  },
];

export default function HowItWorks() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <AppHeader currentPage="how-it-works" />

      <main className="max-w-7xl mx-auto px-4 pt-20 pb-24">
        {/* Hero */}
        <PageHero
          title="How It Works"
          subtitle="Everything you need to know to start collecting and trading NFTs on FLOW"
          badge={
            <Badge className="bg-primary/10 text-primary border-primary/30 px-3 py-1">
              Quick Guide
            </Badge>
          }
        />

        {/* Step-by-step */}
        <section className="mt-16">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
            Get Started in 4 Simple Steps
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((step) => (
              <Card
                key={step.number}
                className="relative overflow-hidden group"
              >
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl font-black text-primary/20 select-none">
                      {step.number}
                    </span>
                    <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center">
                      <step.icon className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </CardContent>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-accent opacity-0 group-hover:opacity-100 transition-opacity" />
              </Card>
            ))}
          </div>
        </section>

        {/* Bonding Curve */}
        <section className="mt-20">
          <h2 className="text-2xl font-bold text-foreground mb-3 text-center">
            The Bonding Curve
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-10">
            Each NFT has a price that{" "}
            <span className="text-primary font-semibold">
              increases by 25% + $0.01
            </span>{" "}
            after every sale. The earlier you buy, the lower the price.
          </p>

          {/* Price Ladder Visual */}
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-6 space-y-3">
              {bondingPrices.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-14 shrink-0 text-right">
                    {item.sale}
                  </span>
                  <div className="flex-1 relative h-8">
                    <div
                      className="absolute inset-y-0 left-0 rounded-md bg-gradient-to-r from-primary/80 to-accent/60 transition-all duration-500"
                      style={{ width: item.width }}
                    />
                    <span className="absolute inset-y-0 flex items-center pl-3 text-sm font-semibold text-primary-foreground drop-shadow-sm">
                      {item.price}
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Example Box */}
          <Card className="max-w-2xl mx-auto mt-6 border-primary/30 bg-primary/5">
            <CardContent className="p-5 flex gap-3 items-start">
              <div className="w-8 h-8 rounded-full bg-primary/20 grid place-items-center shrink-0 mt-0.5">
                <ArrowRight className="w-4 h-4 text-primary" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">
                  Example
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  You mint an NFT for <strong className="text-foreground">free</strong>.
                  Someone buys it for{" "}
                  <strong className="text-foreground">$1.00</strong>. The next
                  buyer pays{" "}
                  <strong className="text-foreground">$1.26</strong>. Then{" "}
                  <strong className="text-foreground">$1.59</strong>. The price
                  keeps climbing with each sale — rewarding early supporters.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Burn Fee Explanation */}
        <section className="mt-12">
          <Card className="max-w-2xl mx-auto border-orange-500/30 bg-orange-500/5">
            <CardContent className="p-5 flex gap-3 items-start">
              <div className="w-8 h-8 rounded-full bg-orange-500/20 grid place-items-center shrink-0 mt-0.5">
                <span className="text-lg">🔥</span>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">
                  12% Burn Fee
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  A <strong className="text-foreground">12% fee</strong> is applied on top of every sale and sent to the{" "}
                  <strong className="text-foreground">burn wallet</strong>. This means the seller receives the full listed price,
                  while the buyer pays the price + 12%. The burned EMON tokens are permanently removed from circulation,
                  reducing the total supply over time and supporting the long-term value of EMON.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* EMON Token */}
        <section className="mt-20">
          <h2 className="text-2xl font-bold text-foreground mb-3 text-center">
            The EMON Token
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-8">
            Everything you need to know about the currency that powers FLOW.
          </p>

          <div className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
            <Card>
              <CardContent className="p-5 space-y-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center">
                  <Coins className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-base font-semibold text-foreground">What is EMON?</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  EMON is the native utility token of the FLOW ecosystem, built on Solana. All NFT purchases and sales on FLOW are transacted in EMON.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5 space-y-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center">
                  <ArrowRight className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-base font-semibold text-foreground">How to Get EMON</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  You can swap SOL for EMON on{" "}
                  <a
                    href="https://raydium.io/swap/?inputMint=sol&outputMint=76kfPfv218KhNQGx7gpC1GR9UWLZHBGSimhyK79ZpUMP"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline underline-offset-2"
                  >
                    Raydium
                  </a>
                  . You only need EMON when you want to buy an NFT — minting your first one is free.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5 space-y-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center">
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-base font-semibold text-foreground">USD Display</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  All NFT prices are denominated in EMON but displayed in USD for convenience. The conversion is calculated in real time based on market data.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5 space-y-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center">
                  <span className="text-lg">🔥</span>
                </div>
                <h3 className="text-base font-semibold text-foreground">Deflationary by Design</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  A 12% fee is applied on every sale and sent to the burn wallet. These EMON tokens are permanently removed from circulation, reducing total supply over time.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-20 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-2">
            {faqItems.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-border/50 rounded-lg px-4 data-[state=open]:border-primary/30"
              >
                <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* CTA */}
        <section className="mt-20 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-3">
            Ready to Start?
          </h2>
          <p className="text-muted-foreground mb-6">
            Connect your wallet and mint your first NFT for free.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              variant="hero"
              size="lg"
              onClick={() => navigate("/marketplace")}
            >
              Explore Marketplace
            </Button>
            <Button
              variant="heroSecondary"
              size="lg"
              onClick={() => navigate("/emon")}
            >
              Learn About EMON
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
